/**
 * GL-DESIGN-V3-2026Q4 — AppShell with responsive v3 NavRail.
 *
 * - Desktop (≥1024px): 232px left sidebar with icon + label + raised shadow
 * - iPad (768-1024px): 84px floating raised icon rail on the left
 * - iPhone (<768px): 70px floating bottom pill bar
 *
 * Preserves offline-sync banner + queued-count behavior from v1 shell.
 */
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  GaugeIcon,
  PlusCircleIcon,
  BellRingingIcon,
  FileTextIcon,
  GearIcon,
  CloudArrowUpIcon,
  WarningCircleIcon,
  MapPinIcon,
} from '@phosphor-icons/react';
import { useOnline } from '@/lib/useOnline';
import { onSyncEvent, queueSize, flushQueue } from '@/lib/offline';
import { useAuth } from '@/lib/auth';
import { V3 } from '@/components/v3/kit';

const NAV_ITEMS = [
  { to: '/',          label: 'Dashboard', Icon: GaugeIcon, testid: 'nav-dashboard' },
  { to: '/new',       label: 'New',       Icon: PlusCircleIcon, testid: 'nav-new' },
  { to: '/reminders', label: 'Reminders', Icon: BellRingingIcon, testid: 'nav-reminders' },
  { to: '/matrix',    label: 'Matrix',    Icon: MapPinIcon, testid: 'nav-matrix' },
  { to: '/reports',   label: 'Reports',   Icon: FileTextIcon, testid: 'nav-reports' },
  { to: '/settings',  label: 'Settings',  Icon: GearIcon, testid: 'nav-settings' },
];

function useDevice() {
  const [d, setD] = useState('desktop');
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      if (w < 768) setD('iphone');
      else if (w < 1024) setD('ipad');
      else setD('desktop');
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return d;
}

export default function AppShell({ children }) {
  const online = useOnline();
  const auth = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const device = useDevice();
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    let mounted = true;
    queueSize().then((n) => mounted && setPending(n));
    const off = onSyncEvent((evt) => {
      if (!mounted) return;
      if (evt.type === 'start') setSyncing(true);
      if (evt.type === 'done') {
        setSyncing(false);
        queueSize().then((n) => mounted && setPending(n));
      }
    });
    const interval = setInterval(() => queueSize().then((n) => mounted && setPending(n)), 4000);
    return () => { mounted = false; off(); clearInterval(interval); };
  }, []);

  useEffect(() => {
    if (online && pending > 0 && !syncing) flushQueue();
  }, [online, pending, syncing]);

  const isDesktop = device === 'desktop';
  const isIPhone = device === 'iphone';
  const isIPad = device === 'ipad';
  const padLeft = isDesktop ? 272 : isIPad ? 124 : 0;
  const padBottom = isIPhone ? 102 : 0;

  const initials = (auth?.user?.name || auth?.user?.email || 'VL')
    .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'VL';

  return (
    <div style={{ background: V3.surface, minHeight: '100vh' }} data-testid="app-shell">
      {/* -------- Offline / Sync banners -------- */}
      {!online && (
        <div
          data-testid="offline-banner"
          style={{
            position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
            background: V3.gold, color: V3.navy, padding: '10px 18px', borderRadius: 999,
            display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700,
            zIndex: 90, boxShadow: '0 10px 20px -10px rgba(20,38,84,.5)',
          }}
        >
          <WarningCircleIcon size={16} weight="fill" />
          Offline · {pending} changes waiting to sync
        </div>
      )}
      {online && pending > 0 && (
        <div
          data-testid="sync-badge"
          style={{
            position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
            background: V3.navy, color: '#fff', padding: '8px 16px', borderRadius: 999,
            display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600,
            zIndex: 90, letterSpacing: '0.04em', textTransform: 'uppercase',
          }}
        >
          <CloudArrowUpIcon size={14} weight="bold" className={syncing ? 'gl-spin' : ''} />
          {syncing ? `Syncing ${pending}…` : `${pending} pending sync`}
        </div>
      )}

      {/* -------- NavRail (desktop/iPad left; iPhone bottom) -------- */}
      {!isIPhone && (
        <nav
          style={{
            position: 'fixed', top: 20, bottom: 20, left: 20,
            width: isDesktop ? 232 : 84,
            borderRadius: 30,
            background: V3.surface,
            boxShadow: V3.raisedLg,
            display: 'flex', flexDirection: 'column',
            alignItems: isDesktop ? 'stretch' : 'center',
            padding: isDesktop ? '20px 14px' : '20px 10px',
            gap: 10, zIndex: 40,
          }}
        >
          {isDesktop && (
            <div style={{ padding: '0 10px 6px' }}>
              <div style={{ fontFamily: V3.mono, fontSize: 11, letterSpacing: '0.18em', color: V3.gold, fontWeight: 700, textTransform: 'uppercase' }}>GigLine</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: V3.navy, letterSpacing: '-0.02em' }}>Field</div>
            </div>
          )}
          {NAV_ITEMS.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.to === '/'} data-testid={it.testid}
              className={({ isActive }) => `v3-nav-link ${isActive ? 'active' : ''}`}
              style={({ isActive }) => ({
                height: 52, width: isDesktop ? 'auto' : 52,
                alignSelf: isDesktop ? 'stretch' : 'center',
                borderRadius: isDesktop ? 16 : '50%',
                display: 'inline-flex', alignItems: 'center',
                justifyContent: isDesktop ? 'flex-start' : 'center',
                gap: 12, padding: isDesktop ? '0 14px' : 0,
                textDecoration: 'none',
                background: isActive ? V3.navy : 'transparent',
                color: isActive ? '#fff' : V3.muted,
                boxShadow: isActive ? '0 10px 20px -10px rgba(20,38,84,.6)' : 'none',
              })}
            >
              <it.Icon size={22} weight="bold" />
              {isDesktop && <span style={{ fontSize: 14.5, fontWeight: 600 }}>{it.label}</span>}
            </NavLink>
          ))}
          <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 12,
                        justifyContent: isDesktop ? 'flex-start' : 'center',
                        padding: isDesktop ? '12px 8px 4px' : '12px 0 4px' }}>
            <button type="button" onClick={() => nav('/settings')}
              style={{
                width: 44, height: 44, borderRadius: '50%',
                background: V3.tint, color: V3.navy,
                fontSize: 14, fontWeight: 700, border: 0, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
              data-testid="nav-profile"
            >{initials}</button>
            {isDesktop && (
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: V3.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {auth?.user?.name || 'Vince'}
                </span>
                <button onClick={auth.logout} style={{ fontSize: 11, color: V3.muted, background: 'transparent', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer' }}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </nav>
      )}

      {/* -------- iPhone bottom pill nav -------- */}
      {isIPhone && (
        <nav
          style={{
            position: 'fixed', bottom: 14, left: 14, right: 14, height: 68,
            background: V3.navy, borderRadius: 999,
            display: 'flex', alignItems: 'center', justifyContent: 'space-around',
            padding: '0 16px', zIndex: 50,
            boxShadow: '0 14px 28px -14px rgba(20,38,84,.6)',
          }}
          data-testid="iphone-bottom-nav"
        >
          {NAV_ITEMS.filter((it) => ['/','/new','/reminders','/settings'].includes(it.to)).map((it) => (
            <NavLink key={it.to} to={it.to} end={it.to === '/'} data-testid={`iphone-${it.testid}`}
              style={({ isActive }) => ({
                width: 46, height: 46, borderRadius: '50%',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                textDecoration: 'none',
                background: isActive ? V3.gold : 'transparent',
                color: isActive ? V3.navy : '#fff',
                opacity: isActive ? 1 : 0.9,
              })}
            >
              <it.Icon size={22} weight="bold" />
            </NavLink>
          ))}
        </nav>
      )}

      {/* -------- Main content -------- */}
      <main style={{ paddingLeft: padLeft, paddingBottom: padBottom, minHeight: '100vh' }}>
        {children}
      </main>
    </div>
  );
}
